/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { cachedGet } from '../utils/apiCache';
import { Navigation, ChevronRight, Mail, ExternalLink, Map, Calendar, MapPin, Pencil, Building2, Coffee, Moon, Tent, ArrowRight, Smartphone, Download, X, Users, Clock, User, GripVertical, Check, RotateCcw } from 'lucide-react';
import { SortableSection } from '../components/landing/SortableSection';
import { Navbar } from '../components/Navbar';
import { ProfileWizard } from '../components/ProfileWizard';
const ClubMap = lazy(() => import('../components/ClubMap'));
import ShareButton from '../components/ShareButton';
import FeaturedEvents from '../components/FeaturedEvents';
const FrontpageEventsCarousel = lazy(() => import('../components/FrontpageEventsCarousel'));
const FeedCarousel = lazy(() => import('../components/FeedCarousel'));
const FeedCockpitEntry = lazy(() => import('../components/FeedCockpitEntry'));
const PublicFeed = lazy(() => import('../components/PublicFeed'));
const NewsletterSignup = lazy(() => import('../components/NewsletterSignup'));
const EventsSection = lazy(() => import('../components/EventsSection'));
const OffersSection = lazy(() => import('../components/OffersSection'));
const PopularPitstops = lazy(() => import('../components/PopularPitstops'));
import { EditableBlock } from '../components/EditableBlock';
import { PermanentRadiusSlider, NearestClubCard, useUserLocation, RADIUS_STEPS, DEFAULT_RADIUS_INDEX, calculateDistance } from '../components/RadiusSearch';
import { BannerDisplay } from '../components/BannerDisplay';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { FarliCapabilities } from '../components/FarliCapabilities';
import { toast } from 'sonner';
import { ScrollReveal } from '../hooks/useScrollEffects';
import { getImageUrl } from '../utils/imageUrl';

const countryToFlag = (code) => {
  if (!code || code.length !== 2) return '';
  const codePoints = code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_ORDER = [
  "activity_ticker", "banner_top",
  "farli", "open_now", "events", "public_feed", "newsletter",
  "pitstop_cta", "overnight_free", "overnight_paid", "download_app",
];

// Bumped to v6: stats + rider_events now hardcoded after map section
const SECTION_ORDER_CACHE_KEY = "baremc_landing_section_order_v6";

// Synchronously read the last-known order from localStorage so the FIRST render
// is already correct. Avoids a layout jump when the server response lands.
const readCachedOrder = () => {
  try {
    const raw = localStorage.getItem(SECTION_ORDER_CACHE_KEY);
    if (!raw) return DEFAULT_ORDER;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch (_e) { /* ignore */ }
  return DEFAULT_ORDER;
};

const SECTION_LABELS = {
  activity_ticker: "Aktivitets-ticker",
  banner_top: "Banner (top)",
  stats: "Statistik",
  rider_events: "Rider Events",
  map: "Kort",
  farli: "Farli AI",
  open_now: "Åbne nu",
  events: "Events-sektioner",
  public_feed: "Offentligt feed",
  newsletter: "Nyhedsbrev & Del",
  pitstop_cta: "Registrér dit sted",
  overnight_free: "Gratis overnatning",
  overnight_paid: "Betalt overnatning",
  download_app: "Download app",
};

// ─── Time-based greeting ───
const getTimeGreeting = (translate) => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return translate('landing.greetingMorning');
  if (h >= 12 && h < 17) return translate('landing.greetingAfternoon');
  if (h >= 17 && h < 22) return translate('landing.greetingEvening');
  return translate('landing.greetingNight');
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  
  const navigateProtected = (path) => {
    if (user) {
      navigate(path);
    } else {
      setShowLoginPrompt(true);
    }
  };
  const isAdmin = user?.is_site_admin;
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [totalEventsCount, setTotalEventsCount] = useState(0);
  const [openClubs, setOpenClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [overnightClubs, setOvernightClubs] = useState([]);
  const [showFarliCapabilities, setShowFarliCapabilities] = useState(false);
  const [expandedStat, setExpandedStat] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [totalCheckins, setTotalCheckins] = useState(null);
  const [totalOpenNow, setTotalOpenNow] = useState(null);
  
  // Section ordering state — initialised from localStorage so first render is correct
  const [sectionOrder, setSectionOrder] = useState(readCachedOrder);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [savedOrder, setSavedOrder] = useState(readCachedOrder);
  const [orderLoaded, setOrderLoaded] = useState(false);
  
  const { userLocation, locationLoading, requestLocation } = useUserLocation();
  const [radiusIndex, setRadiusIndex] = useState(DEFAULT_RADIUS_INDEX);
  const currentRadius = RADIUS_STEPS[radiusIndex];

  const bgRef = useRef(null);
  const overlayRef = useRef(null);

  const handleScroll = useCallback(() => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const progress = Math.min(window.scrollY / maxScroll, 1);
    if (bgRef.current) {
      const scale = 1 + progress * 2.0;
      const translateY = progress * -35;
      bgRef.current.style.transform = `scale(${scale}) translateY(${translateY}%)`;
    }
    if (overlayRef.current) {
      const opacity = 0.50 + progress * 0.28;
      const r = Math.round(6 - progress * 3);
      const g = Math.round(78 - progress * 30);
      const b = Math.round(59 - progress * 20);
      overlayRef.current.style.background = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Fetch section order — only use server order if it's the new format (map first).
  // Old cached orders on the server will be ignored so the DEFAULT_ORDER v3 takes effect.
  useEffect(() => {
    axios.get(`${API_URL}/settings/landing-order`).then(res => {
      const serverOrder = res.data?.order;
      if (Array.isArray(serverOrder) && serverOrder.length > 0) {
        // v4 guard: only accept orders that don't contain 'map' or 'stats'
        // (those are v1/v2/v3 orders — stale).
        const isNewFormat = !serverOrder.includes('map') && !serverOrder.includes('stats');
        if (isNewFormat) {
          try { localStorage.setItem(SECTION_ORDER_CACHE_KEY, JSON.stringify(serverOrder)); } catch (_e) { /* ignore */ }
          setSectionOrder(prev => (JSON.stringify(prev) === JSON.stringify(serverOrder) ? prev : serverOrder));
          setSavedOrder(serverOrder);
        }
        // else: stale server order → keep DEFAULT_ORDER that was set on init
      }
      setOrderLoaded(true);
    }).catch(() => setOrderLoaded(true));
  }, []);

  useEffect(() => { fetchData(); }, []);

  // Fetch homepage stats (check-ins + open-now). Cache-buster param ensures
  // any CDN/edge cache never serves a stale count after new clubs/pitstops
  // are added, so the figure always reflects the live database.
  useEffect(() => {
    axios.get(`${API_URL}/stats`, { params: { t: Date.now() } }).then(res => {
      setTotalCheckins(res.data.total_checkins ?? null);
      setTotalOpenNow(res.data.open_now ?? null);
      if (res.data.total_events > 0) setTotalEventsCount(res.data.total_events);
    }).catch(() => { /* stats fallback to clubs data */ });
  }, []);

  const fetchData = async (retryCount = 0) => {
    const cached = (() => { try { return JSON.parse(localStorage.getItem('baremc_landing_cache')); } catch (_e) { return null; } })();
    
    // Show cached data IMMEDIATELY
    if (cached?.clubs?.length > 0 && clubs.length === 0) {
      setClubs(cached.clubs);
      setOpenClubs((cached.clubs || []).filter(cl => cl.is_open));
      setEvents((cached.events || []).slice(0, 3));
      setAllEvents(cached.events || []);
      setTotalEventsCount((cached.events || []).length);
      setOvernightClubs(cached.overnight || []);
      setLoading(false);
    }

    // If cache is fresh enough, skip network
    if (cached?.ts && Date.now() - cached.ts < 120000 && cached.clubs?.length > 0) {
      setLoading(false);
      return;
    }

    try {
      // PHASE 1: Load clubs first (what user sees on the map)
      const clubsRes = await cachedGet(`${API_URL}/clubs/slim`, 120000).catch(() => null);
      const clubsData = clubsRes && Array.isArray(clubsRes.data) ? clubsRes.data : [];
      
      if (clubsData.length === 0 && retryCount < 3) {
        setTimeout(() => fetchData(retryCount + 1), 2000 * (retryCount + 1));
        return;
      }
      
      if (clubsData.length > 0) {
        setClubs(clubsData);
        setOpenClubs(clubsData.filter(c => c.is_open));
        setLoading(false); // Page is usable now!
      }

      // PHASE 2: Load events + pitstops in background (non-blocking)
      const [eventsRes, pitstopsRes] = await Promise.all([
        cachedGet(`${API_URL}/events`, 120000).catch(() => null),
        cachedGet(`${API_URL}/pitstops`, 120000).catch(() => null)
      ]);
      const eventsData = eventsRes && Array.isArray(eventsRes.data) ? eventsRes.data : [];
      if (eventsData.length > 0 || !eventsRes) {
        setEvents(eventsData.slice(0, 3));
        setAllEvents(eventsData);
        setTotalEventsCount(eventsData.length);
      }
      const pitstopData = pitstopsRes && Array.isArray(pitstopsRes.data) ? pitstopsRes.data : [];
      const overnightPitstops = pitstopData
        .filter(p => p.status === 'approved' && (p.category === 'overnatning' || p.category === 'camping'))
        .map(p => ({
          ...p,
          is_pitstop: true,
          overnight_free: p.price_info ? p.price_info.toLowerCase().includes('gratis') : false,
          overnight_price: p.price_info ? parseFloat(p.price_info.replace(/[^0-9]/g, '')) || 0 : 0,
        }));
      const enrichedClubs = clubsData.map(c => ({
        ...c,
        overnight_free: c.overnight_free || (
          !c.overnight_price && (c.facilities || []).some(f => ['beds', 'tent', 'caravan'].includes(f))
        ),
      }));
      const allOvernight = [
        ...enrichedClubs.filter(c => c.overnight_free || (c.overnight_price && c.overnight_price > 0)),
        ...overnightPitstops,
      ].sort((a, b) => {
        if (a.overnight_free && !b.overnight_free) return -1;
        if (!a.overnight_free && b.overnight_free) return 1;
        return (a.overnight_price || 0) - (b.overnight_price || 0);
      });
      setOvernightClubs(allOvernight);

      if (clubsData.length > 0) {
        try { localStorage.setItem('baremc_landing_cache', JSON.stringify({ clubs: clubsData, events: eventsData, overnight: allOvernight, ts: Date.now() })); } catch (_e) {}
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (!cached?.clubs?.length) setFetchFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Move section up/down
  const moveSection = (id, direction) => {
    setSectionOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const saveOrder = async () => {
    try {
      await axios.put(`${API_URL}/settings/landing-order`, { order: sectionOrder }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSavedOrder([...sectionOrder]);
      // Cache locally so next page load is instantly correct, no flash
      try { localStorage.setItem(SECTION_ORDER_CACHE_KEY, JSON.stringify(sectionOrder)); } catch (_e) { /* ignore */ }
      setIsEditingLayout(false);
      toast.success(t('layout.layoutSaved'));
    } catch (_saveErr) {
      toast.error(t('layout.layoutSaveFailed'));
    }
  };

  const cancelEdit = () => {
    setSectionOrder([...savedOrder]);
    setIsEditingLayout(false);
  };

  // ─── Section renderers ───
  const renderSection = (id) => {
    switch (id) {
      case 'activity_ticker':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <Suspense fallback={<div style={{minHeight:'180px', background:'transparent'}} />}><FeedCockpitEntry /></Suspense>
          </ErrorBoundary>
        );

      case 'banner_top':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <div className="max-w-3xl mx-auto px-4">
              <BannerDisplay position="top" />
            </div>
          </ErrorBoundary>
        );

      case 'stats':
        return null; // Hardcoded directly after MapSection

      case 'rider_events':
        return null; // Hardcoded directly after MapSection

      case 'map':
        return null; // Map is now rendered as a fixed section above (after club ticker)

      case 'farli':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <FarliSection language={language} t={t} showFarliCapabilities={showFarliCapabilities} setShowFarliCapabilities={setShowFarliCapabilities} />
          </ErrorBoundary>
        );

      case 'open_now':
        return openClubs.length > 0 ? (
          <ErrorBoundary key={id} fallback={null}>
            <OpenNowSection openClubs={openClubs} t={t} />
          </ErrorBoundary>
        ) : null;

      case 'events':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <Suspense fallback={<div className="py-12 text-center text-zinc-600 text-sm">{t('landing.eventsLoading')}</div>}>
              <div>
                <ErrorBoundary fallback={null}><EventsSection /></ErrorBoundary>
                <ErrorBoundary fallback={null}><FrontpageEventsCarousel /></ErrorBoundary>
                <ErrorBoundary fallback={null}><OffersSection /></ErrorBoundary>
                <ErrorBoundary fallback={null}><PopularPitstops /></ErrorBoundary>
                <div className="max-w-3xl mx-auto px-4 py-6">
                  <ErrorBoundary fallback={null}><BannerDisplay position="between-sections" /></ErrorBoundary>
                </div>
                <ErrorBoundary fallback={null}><FeaturedEvents /></ErrorBoundary>
              </div>
            </Suspense>
          </ErrorBoundary>
        );

      case 'public_feed':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <Suspense fallback={<div style={{minHeight:'200px'}} />}><PublicFeed /></Suspense>
          </ErrorBoundary>
        );

      case 'newsletter':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <Suspense fallback={<div style={{minHeight:'80px'}} />}><NewsletterShareSection t={t} /></Suspense>
          </ErrorBoundary>
        );

      case 'pitstop_cta':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <PitstopCTASection navigate={navigate} onOpenWizard={() => setShowProfileWizard(true)} />
          </ErrorBoundary>
        );

      case 'overnight_free':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <OvernightFreeSection overnightClubs={overnightClubs} navigate={navigate} />
          </ErrorBoundary>
        );

      case 'overnight_paid':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <OvernightPaidSection overnightClubs={overnightClubs} navigate={navigate} />
          </ErrorBoundary>
        );

      case 'download_app':
        return (
          <ErrorBoundary key={id} fallback={null}>
            <DownloadAppSection navigate={navigate} />
          </ErrorBoundary>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative bg-[#042f24] pb-28">
      {/* Fixed background */}
      <div className="fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div
          ref={bgRef}
          className="absolute inset-0 will-change-transform"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1544141687-4a0477ba4ccc?w=1920&q=80&auto=format')",
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            transformOrigin: 'center 70%',
          }}
        />
        <div ref={overlayRef} className="absolute inset-0" />
        {/* Static gradient deepens the lower content area — reduces background bleed-through on mobile */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 0%, transparent 22%, rgba(3,18,14,0.38) 50%, rgba(2,12,10,0.62) 100%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        
        {/* ─── TOP-LEFT GREETING — personal, time-based, premium ─── */}
        <div className="px-5 pt-20 pb-3" data-testid="landing-topleft-greeting">
          <div
            className="inline-flex flex-col items-start gap-0.5"
            style={{
              background: 'rgba(10, 26, 18, 0.52)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              border: '1px solid rgba(232,220,192,0.11)',
              borderRadius: '14px',
              padding: '10px 16px 10px 14px',
            }}
          >
            <p
              className="text-left text-[13px] font-semibold tracking-wide uppercase"
              style={{ color: 'rgba(45,255,136,0.85)', letterSpacing: '0.12em' }}
            >
              {getTimeGreeting(t)}
            </p>
            {user?.name && (
              <p
                className="text-left text-[22px] font-black leading-none"
                style={{ color: 'rgba(242,235,216,0.97)', textShadow: '0 2px 12px rgba(0,0,0,0.7)', letterSpacing: '-0.01em' }}
              >
                {user.name.split(' ')[0]}
              </p>
            )}
          </div>
        </div>

        {/* Hero — rolled back to a calmer, more stable look.
            Pulsing live-dot pill removed, glow reduced, CTAs are clean
            outlined / filled buttons (the version users said worked
            best). Focus is now on the feed below — hero is just the
            opening note. */}
        <section className="pt-3 pb-14 sm:pb-20 px-5">
          <div className="max-w-3xl mx-auto text-center">
            {/* LIVE-stats pill removed per user request */}
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 leading-[1.08] tracking-tight"
              style={{ textShadow: '0 3px 12px rgba(0,0,0,0.65)' }}
            >
              {t('landing.heroTitleLine1')}
              <br />
              <span className="text-emerald-300 italic">
                {t('landing.heroTitleLine2')}
              </span>
            </h1>
            <p
              className="text-base sm:text-lg text-white/70 mb-8 max-w-md mx-auto leading-relaxed"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
            >
              <EditableBlock pageId="landing" sectionId="heroDesc" defaultText={t('landing.heroDesc')} />
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/kort?findNearest=true')}
                className="inline-flex items-center gap-2.5 bg-emerald-400 text-zinc-900 font-bold text-base px-7 py-3 rounded-full hover:bg-emerald-300 transition-colors shadow-md shadow-emerald-400/20"
                data-testid="hero-find-nearest-btn"
              >
                <Navigation className="w-5 h-5" />
                {t('landing.findNearestClub')}
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowProfileWizard(true)}
                className="inline-flex items-center gap-2.5 border-2 border-white/20 text-white font-bold text-base px-7 py-3 rounded-full hover:border-emerald-400/50 hover:text-emerald-300 transition-colors"
                data-testid="hero-create-profile-btn"
              >
                <Building2 className="w-5 h-5" />
                {t('landing.createProfile')}
              </button>
            </div>
          </div>
        </section>

        {/* ═══ ANIMATED CLUB LOGOS TICKER ═══
            Shows real club logos in a continuously scrolling strip.
            Creates the "living" feeling the user requested — visible
            immediately when the page loads, before scrolling. */}
        {clubs.length > 0 && (
          <div
            className="relative overflow-hidden py-7"
            style={{
              maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            }}
            data-testid="landing-club-ticker"
            aria-hidden="true"
          >
            <div
              className="flex items-center gap-6 animate-marquee"
              style={{
                width: 'max-content',
                animation: 'marqueeScroll 32s linear infinite',
              }}
            >
              {/* Two copies for seamless loop */}
              {[...clubs, ...clubs].filter(c => c.image_url).slice(0, 40).map((club, idx) => (
                <Link
                  key={`${club.id}-${idx}`}
                  to={`/klubber/${club.id}`}
                  className="flex items-center gap-2.5 flex-shrink-0 group"
                  style={{ pointerEvents: idx < clubs.length ? 'auto' : 'none' }}
                  data-testid={idx < clubs.length ? `ticker-club-${club.id}` : undefined}
                >
                  <div
                    className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border"
                    style={{ borderColor: 'rgba(45,255,136,0.2)' }}
                  >
                    <img
                      src={getImageUrl(club.image_url)}
                      alt={club.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={e => { e.target.style.display='none'; }}
                    />
                  </div>
                  <span
                    className="text-[13px] font-semibold whitespace-nowrap"
                    style={{ color: 'rgba(242,235,216,0.65)' }}
                  >
                    {club.name}
                  </span>
                  <span className="text-white/15 text-sm ml-1">·</span>
                </Link>
              ))}
            </div>
            <style>{`
              @keyframes marqueeScroll {
                from { transform: translateX(0); }
                to   { transform: translateX(-50%); }
              }
            `}</style>
          </div>
        )}

        {/* ═══ MAP SECTION — fixed position right after club logos ═══ */}
        <ErrorBoundary fallback={
          <div className="max-w-3xl mx-auto px-4 py-6 text-center">
            <a href="/kort" className="text-emerald-400 text-sm underline">{t('gen.openMap') || 'Se fuld kort'}</a>
          </div>
        }>
        <MapSection
          clubs={clubs}
          userLocation={userLocation}
          locationLoading={locationLoading}
          requestLocation={requestLocation}
          radiusIndex={radiusIndex}
          setRadiusIndex={setRadiusIndex}
          currentRadius={currentRadius}
          navigate={navigate}
          t={t}
        />
        </ErrorBoundary>

        {/* ═══ STATS + RYTTER-BEGIVENHEDER — direkte efter kortet ═══ */}
        <ErrorBoundary fallback={null}>
        <StatsSection
          clubs={clubs}
          openClubs={openClubs}
          allEvents={allEvents}
          totalEventsCount={totalEventsCount}
          totalCheckins={totalCheckins}
          totalOpenNow={totalOpenNow}
          expandedStat={expandedStat}
          setExpandedStat={setExpandedStat}
          navigateProtected={navigateProtected}
        />
        </ErrorBoundary>
        <ErrorBoundary fallback={null}>
        <RiderEventsSection allEvents={allEvents} navigateProtected={navigateProtected} />
        </ErrorBoundary>

        {/* ═══ FEED CAROUSEL — horizontal scrolling posts ═══ */}
        <ErrorBoundary fallback={null}>
        <FeedCarousel />
        </ErrorBoundary>

        {/* ═══ LANDING CLUB CARDS — "Klubber tæt på dig" horizontal scroll ═══ */}
        {clubs.length > 0 && (
          <div className="py-8" data-testid="landing-club-cards">
            <div className="flex items-center justify-between px-4 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                <h3 className="text-white font-bold text-[16px]">{t('landing.clubsNearYou')}</h3>
              </div>
              <Link to="/klubber" className="text-emerald-400 text-[13px] font-semibold">{t('landing.seeAll')}</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 px-4 scrollbar-hide snap-x snap-mandatory">
              {clubs.slice(0, 8).map(club => (
                <Link
                  key={club.id}
                  to={`/klubber/${club.id}`}
                  className="flex-shrink-0 snap-start rounded-xl overflow-hidden active:scale-95 transition-transform"
                  style={{
                    width: 160,
                    background: 'rgba(22, 22, 26, 0.88)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}
                  data-testid={`landing-club-card-${club.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative" style={{ height: 100 }}>
                    {/* Fallback always rendered — synlig hvis billede mangler/fejler */}
                    <div
                      className="absolute inset-0 w-full h-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #064e3b 0%, #0d7a5f 100%)' }}
                    >
                      <span className="text-white font-black text-3xl">
                        {(club.name || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    {club.image_url && (
                      <img
                        className="absolute inset-0 w-full h-full object-cover"
                        src={getImageUrl(club.image_url)}
                        alt={club.name}
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    {club.is_open !== undefined && (
                      <div
                        className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[11px] font-bold"
                        style={{
                          background: club.is_open ? 'rgba(45,255,136,0.85)' : 'rgba(0,0,0,0.65)',
                          color: club.is_open ? '#0a1410' : 'rgba(255,255,255,0.5)',
                          border: club.is_open ? 'none' : '1px solid rgba(255,255,255,0.15)',
                        }}
                      >
                        {club.is_open ? t('landing.openShort') : t('landing.closedShort')}
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="px-2.5 py-2.5">
                    <p className="text-white text-[13px] font-bold truncate">{club.name}</p>
                    {club.city && <p className="text-zinc-500 text-[12px] truncate mt-0.5">{club.city}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ═══ RIDES HIGHLIGHT — "Se seneste ture" visual card ═══ */}
        <div className="px-4 pb-8" data-testid="landing-rides-banner">
          <Link
            to="/mine-ture"
            className="block relative rounded-2xl overflow-hidden active:scale-[0.99] transition-transform"
            style={{
              background: 'linear-gradient(135deg, #0c1811 0%, #0f2218 50%, #071510 100%)',
              border: '1px solid rgba(45,255,136,0.18)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(to right, transparent, rgba(45,255,136,0.5), transparent)' }} />
            <div className="flex items-center gap-4 px-5 py-5">
              <div className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(45,255,136,0.12)', border: '2px solid rgba(45,255,136,0.35)', boxShadow: '0 0 20px rgba(45,255,136,0.18)' }}>
                <svg viewBox="0 0 24 24" fill="#2DFF88" className="w-6 h-6 ml-0.5"><path d="M8 5v14l11-7z" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase mb-1" style={{ color: 'rgba(45,255,136,0.8)' }}>{t('landing.ridesLabel')}</p>
                <p className="text-[18px] font-black tracking-tight leading-tight" style={{ color: '#F2EBD8' }}>{t('landing.ridesTitle')}</p>
                <p className="text-[12px] mt-1 font-medium" style={{ color: 'rgba(242,235,216,0.45)' }}>{t('landing.ridesSub')}</p>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(45,255,136,0.6)" strokeWidth="2" className="w-5 h-5 flex-shrink-0"><path d="M9 18l6-6-6-6" /></svg>
            </div>
          </Link>
        </div>

        {/* Content body — subtle backdrop reduces background bleed-through for all sections below the feed */}
        <div style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(4,22,16,0.20) 8%, rgba(3,16,12,0.28) 100%)' }}>

        {/* Admin layout editor toolbar */}
        {isAdmin && (
          <div className="fixed bottom-6 right-6 z-[60] flex items-center gap-2" data-testid="layout-editor-toolbar">
            {isEditingLayout ? (
              <>
                <button
                  onClick={cancelEdit}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-800 border border-zinc-600 text-white text-sm font-semibold hover:bg-zinc-700 transition-all shadow-2xl"
                  data-testid="layout-cancel-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('common.cancel')}
                </button>
                <button
                  onClick={saveOrder}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-black text-sm font-bold hover:bg-emerald-400 transition-all shadow-2xl"
                  data-testid="layout-save-btn"
                >
                  <Check className="w-4 h-4" />
                  {t('layout.saveLayout')}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditingLayout(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/90 text-black text-sm font-bold hover:bg-emerald-400 transition-all shadow-2xl backdrop-blur-sm"
                data-testid="layout-edit-btn"
              >
                <GripVertical className="w-4 h-4" />
                {t('layout.editLayout')}
              </button>
            )}
          </div>
        )}

        {/* Sortable sections */}
        {(() => {
          // Check which sections are conditionally hidden
          const hiddenIds = new Set();
          if (openClubs.length === 0) hiddenIds.add('open_now');
          if (overnightClubs.filter(c => c.overnight_free).length === 0) hiddenIds.add('overnight_free');
          if (overnightClubs.filter(c => !c.overnight_free && c.overnight_price > 0).length === 0) hiddenIds.add('overnight_paid');
          const visibleOrder = sectionOrder.filter(id => !hiddenIds.has(id));
          
          return sectionOrder.map(id => {
            if (hiddenIds.has(id)) return null;
            const visibleIdx = visibleOrder.indexOf(id);
            return (
              <SortableSection
                key={id}
                id={id}
                isEditing={isEditingLayout}
                onMoveUp={() => moveSection(id, -1)}
                onMoveDown={() => moveSection(id, 1)}
                isFirst={visibleIdx === 0}
                isLast={visibleIdx === visibleOrder.length - 1}
              >
                {renderSection(id)}
              </SortableSection>
            );
          });
        })()}

        </div>{/* end content body */}

        {/* Footer - always last */}
        <footer className="py-8 px-4 border-t border-white/10 mt-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col items-center gap-4">
              <p className="text-white/40 text-xs font-medium tracking-wider uppercase">&copy; 2026 BareMC</p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                <Link to="/klubber" className="text-white/60 hover:text-emerald-400 transition-colors">{t('nav.findVisit')}</Link>
                <Link to="/kort" className="text-white/60 hover:text-emerald-400 transition-colors">{t('nav.map')}</Link>
                <Link to="/events" className="text-white/60 hover:text-emerald-400 transition-colors">{t('nav.events')}</Link>
                <Link to="/regler" className="text-white/60 hover:text-emerald-400 transition-colors">{t('nav.rules')}</Link>
                <Link to="/kontakt" className="text-white/60 hover:text-emerald-400 transition-colors">{t('nav.contact')}</Link>
              </div>
              <a href="mailto:info@baremc.com" className="text-emerald-400/70 hover:text-emerald-300 text-xs transition-colors">info@baremc.com</a>
            </div>
          </div>
        </footer>
      </div>

      <FarliCapabilities isOpen={showFarliCapabilities} onClose={() => setShowFarliCapabilities(false)} />

      {showLoginPrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" onClick={() => setShowLoginPrompt(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-zinc-900 border border-white/15 rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="login-prompt-modal">
            <button onClick={() => setShowLoginPrompt(false)} className="absolute top-3 right-3 p-1 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10 transition-colors">
              <X className="w-4 h-4" />
            </button>
            <div className="text-center space-y-4">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Users className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">{t('loginPrompt.loginToContinue')}</h3>
                <p className="text-zinc-400 text-sm">{t('loginPrompt.loginDesc')}</p>
              </div>
              <div className="space-y-2.5 pt-1">
                <button onClick={() => navigate('/login')} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-sm transition-all" data-testid="login-prompt-login">
                  {t('loginPrompt.login')}
                </button>
                <button onClick={() => navigate('/register')} className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm transition-all border border-white/10" data-testid="login-prompt-register">
                  {t('loginPrompt.createFreeProfile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ProfileWizard */}
      <ProfileWizard
        open={showProfileWizard}
        onClose={() => setShowProfileWizard(false)}
        onCreateClub={() => {
          setShowProfileWizard(false);
          navigate('/register?type=club');
        }}
      />
    </div>
  );
};

// ─── Extracted section components ───

function StatsSection({ clubs, openClubs, allEvents, totalEventsCount, totalCheckins, totalOpenNow, expandedStat, setExpandedStat, navigateProtected }) {
  const { t } = useLanguage();
  // Display values: use live stats from API when available, fall back to local club data
  const checkinsDisplay = totalCheckins !== null ? totalCheckins : clubs.length;
  const openNowDisplay = totalOpenNow !== null ? totalOpenNow : openClubs.length;
  return (
    <ScrollReveal>
    <section className="py-5 px-4" data-testid="stats-section">
      <div className="max-w-3xl mx-auto">
        {/* Stats bar — three numbers with more breathing room */}
        <div className="flex items-stretch divide-x divide-white/8 rounded-2xl overflow-hidden bg-zinc-900/70 border border-white/8">
          <button
            onClick={() => setExpandedStat(expandedStat === 'clubs' ? null : 'clubs')}
            className={`flex-1 flex flex-col items-center justify-center py-4 gap-1 transition-colors ${expandedStat === 'clubs' ? 'bg-emerald-500/10' : 'hover:bg-white/5'}`}
            data-testid="stat-clubs-btn"
          >
            <span className="text-2xl font-bold text-emerald-400 leading-none">{checkinsDisplay}</span>
            <span className="text-[10px] font-medium text-zinc-500 leading-none">{t('landing.statsCheckins')}</span>
          </button>
          <button
            onClick={() => setExpandedStat(expandedStat === 'open' ? null : 'open')}
            className={`flex-1 flex flex-col items-center justify-center py-4 gap-1 transition-colors ${expandedStat === 'open' ? 'bg-green-500/10' : 'hover:bg-white/5'}`}
            data-testid="stat-open-btn"
          >
            <span className="text-2xl font-bold text-green-500 leading-none">{openNowDisplay}</span>
            <span className="text-[10px] font-medium text-zinc-500 leading-none">{t('landing.statsOpenNow')}</span>
          </button>
          <button
            onClick={() => navigateProtected('/events')}
            className={`flex-1 flex flex-col items-center justify-center py-4 gap-1 transition-colors hover:bg-white/5`}
            data-testid="stat-events-btn"
          >
            <span className="text-2xl font-bold text-emerald-400 leading-none">{totalEventsCount}+</span>
            <span className="text-[10px] font-medium text-zinc-500 leading-none">{t('landing.statsEvents')}</span>
          </button>
        </div>

        {/* Expandable detail panel */}
        <ErrorBoundary fallback={
          <div className="mt-2 p-3 rounded-xl bg-zinc-900/70 border border-white/8 text-center">
            <p className="text-zinc-400 text-xs mb-2">{t('landing.listUnavailable')}</p>
            <button onClick={() => setExpandedStat(null)} className="text-emerald-400 text-xs underline">{t('common.close')}</button>
          </div>
        }>
        {expandedStat && (
          <div className="mt-2 p-3 rounded-xl bg-zinc-900/70 border border-white/8 animate-in fade-in slide-in-from-top-2 duration-200" data-testid={`stat-list-${expandedStat}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white">
                {expandedStat === 'clubs' && t('clubs.newestClubs')}
                {expandedStat === 'open' && t('clubs.clubStatus')}
                {expandedStat === 'events' && t('clubs.upcomingEvents')}
              </h3>
              <button onClick={() => setExpandedStat(null)} className="p-1 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors" data-testid="stat-list-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 scrollbar-thin pr-1">
              {expandedStat === 'clubs' && (
                [...clubs].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 10).map(club => (
                  <button key={club.id} onClick={() => navigateProtected(`/klubber/${club.id}`)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors text-left group" data-testid={`stat-club-${club.id}`}>
                    {club.image_url ? (
                      <img loading="lazy" src={getImageUrl(club.image_url)} alt="" onError={e => { e.currentTarget.style.display='none'; }} className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-white/10" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-emerald-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white truncate group-hover:text-emerald-400 transition-colors">{club.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{club.city || ''} {club.country_code ? countryToFlag(club.country_code) : ''}</div>
                    </div>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${club.status === 'open' ? 'bg-green-500' : club.status === 'opens_later' ? 'bg-yellow-500' : club.status === 'no_clubhouse' ? 'bg-purple-500' : 'bg-red-500'}`} />
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors" />
                  </button>
                ))
              )}
              {expandedStat === 'open' && (() => {
                const realClubs = clubs.filter(c => c.status !== 'no_clubhouse');
                const sorted = [...realClubs].sort((a, b) => {
                  const order = { open: 0, opens_later: 1, closed: 2 };
                  return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                });
                return sorted.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500 text-sm">{t('clubs.noClubhouseFound')}</div>
                ) : (
                  sorted.slice(0, 30).map(club => (
                    <button key={club.id} onClick={() => navigateProtected(`/klubber/${club.id}`)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors text-left group" data-testid={`stat-open-${club.id}`}>
                      {club.image_url ? (
                        <img loading="lazy" src={getImageUrl(club.image_url)} alt="" onError={e => { e.currentTarget.style.display='none'; }} className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-white/10" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-zinc-400" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate group-hover:text-emerald-400 transition-colors">{club.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{club.city || ''} {club.country_code ? countryToFlag(club.country_code) : ''}</div>
                      </div>
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${club.status === 'open' ? 'bg-green-500 animate-pulse' : club.status === 'opens_later' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors" />
                    </button>
                  ))
                );
              })()}
              {expandedStat === 'events' && (() => {
                const now = new Date();
                const upcoming = [...allEvents]
                  .filter(ev => { const d = new Date(ev.event_date || ev.date); return !isNaN(d) && d >= new Date(now.toDateString()); })
                  .sort((a, b) => new Date(a.event_date || a.date) - new Date(b.event_date || b.date))
                  .slice(0, 10);
                const past = [...allEvents]
                  .filter(ev => { const d = new Date(ev.event_date || ev.date); return !isNaN(d) && d < new Date(now.toDateString()); })
                  .sort((a, b) => new Date(b.event_date || b.date) - new Date(a.event_date || a.date));
                const sorted = [...upcoming, ...past].slice(0, 10);
                const formatCountdown = (dateStr) => {
                  const d = new Date(dateStr);
                  if (isNaN(d)) return '';
                  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
                  if (diff === 0) return t('events.today');
                  if (diff === 1) return t('events.tomorrow');
                  if (diff < 0) return t('events.held');
                  if (diff < 7) return t('events.inDays').replace('{count}', diff);
                  if (diff < 14) return t('events.nextWeek');
                  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
                };
                return sorted.map(ev => {
                  const evDate = ev.event_date || ev.date;
                  const isPast = evDate && new Date(evDate) < now;
                  const isUserEv = ev.event_type === 'user';
                  return (
                    <button key={ev.id} onClick={() => navigateProtected(`/events/${ev.id}`)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors text-left group ${isPast ? 'opacity-50' : ''}`} data-testid={`stat-event-${ev.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isPast ? 'bg-zinc-800' : isUserEv ? 'bg-cyan-500/10' : 'bg-emerald-500/10'}`}>
                        <Calendar className={`w-4 h-4 ${isPast ? 'text-zinc-500' : isUserEv ? 'text-cyan-400' : 'text-emerald-400'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-white truncate group-hover:text-emerald-400 transition-colors">{ev.title || ev.name}</span>
                          {isUserEv && <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" title="Rider event" />}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">
                          {evDate ? new Date(evDate).toLocaleDateString(language === 'da' ? 'da-DK' : language === 'de' ? 'de-DE' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : language === 'pl' ? 'pl-PL' : 'en-GB', { day: 'numeric', month: 'short' }) : ''}
                          {ev.club_name ? ` · ${ev.club_name}` : isUserEv ? ` · ${ev.created_by_user_name || 'Rider'}` : ev.location ? ` · ${ev.location}` : ''}
                        </div>
                      </div>
                      {evDate && (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${isPast ? 'bg-zinc-800 text-zinc-500' : isUserEv ? 'bg-cyan-500/10 text-cyan-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                          {formatCountdown(evDate)}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors" />
                    </button>
                  );
                });
              })()}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10">
              {expandedStat === 'clubs' && (
                <button onClick={() => navigateProtected('/klubber')} className="w-full text-center text-sm text-emerald-400 hover:text-emerald-300 font-medium py-2 rounded-lg hover:bg-emerald-500/5 transition-colors" data-testid="stat-see-all-clubs">
                  {t('clubs.seeAllClubs').replace('{count}', clubs.length)}
                </button>
              )}
              {expandedStat === 'open' && (
                <button onClick={() => navigateProtected('/kort')} className="w-full text-center text-sm text-emerald-400 hover:text-emerald-300 font-medium py-2 rounded-lg hover:bg-emerald-500/5 transition-colors" data-testid="stat-see-day-filter">
                  {t('clubs.seeDayFilter')}
                </button>
              )}
              {expandedStat === 'events' && (
                <button onClick={() => navigateProtected('/events')} className="w-full text-center text-sm text-emerald-400 hover:text-emerald-300 font-medium py-2 rounded-lg hover:bg-emerald-500/5 transition-colors" data-testid="stat-see-all-events">
                  {t('clubs.seeAllEvents')}
                </button>
              )}
            </div>
          </div>
        )}
        </ErrorBoundary>
      </div>
    </section>
    </ScrollReveal>
  );
}

function RiderEventsSection({ allEvents, navigateProtected }) {
  const { t } = useLanguage();
  const now = new Date();
  const userEvents = allEvents.filter(ev => ev.event_type === 'user' && new Date(ev.event_date) >= new Date(now.toDateString()));
  if (userEvents.length === 0) return <div style={{ minHeight: 0 }} />;
  const sorted = [...userEvents].sort((a, b) => new Date(a.event_date) - new Date(b.event_date)).slice(0, 10);

  // 6 fallback motorcycle images (black & white style)
  const FALLBACK_IMAGES = [
    'https://images.unsplash.com/photo-1678141100431-8704cadc21f5?w=300&h=300&fit=crop&q=60',
    'https://images.unsplash.com/photo-1601984736960-a4a05e5ce7c1?w=300&h=300&fit=crop&q=60',
    'https://images.unsplash.com/photo-1750838777807-eb5df5f073df?w=300&h=300&fit=crop&q=60',
    'https://images.unsplash.com/photo-1615193065831-7cae05da97d1?w=300&h=300&fit=crop&q=60',
    'https://images.unsplash.com/photo-1656420731611-ffae337fab6a?w=300&h=300&fit=crop&q=60',
    'https://images.unsplash.com/photo-1588683302128-07cda4a4fb6a?w=300&h=300&fit=crop&q=60',
  ];

  const getEventImage = (ev, index) => {
    if (ev.image_url) return ev.image_url.startsWith('http') ? ev.image_url : `${process.env.REACT_APP_BACKEND_URL}${ev.image_url}`;
    return FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
  };

  // Duplicate cards for seamless loop
  const cards = [...sorted, ...sorted];
  const duration = Math.max(sorted.length * 3, 10); // seconds, based on card count — fast scroll

  return (
    <ScrollReveal delay={50}>
    <section className="py-6" data-testid="rider-events-section">
      <div className="max-w-4xl mx-auto px-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center">
              <User className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">{t('events.riderEvents')}</h2>
              <p className="text-zinc-500 text-xs">{t('events.eventsArrangedByRiders')}</p>
            </div>
          </div>
          <button onClick={() => navigateProtected('/events')} className="text-cyan-400 hover:text-cyan-300 text-xs font-medium flex items-center gap-1 transition-colors" data-testid="see-all-rider-events">
            {t('common.seeAll')} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Marquee scroll container */}
      <div className="overflow-hidden" data-testid="rider-events-marquee">
        <div
          className="flex gap-3 rider-marquee"
          style={{
            width: 'max-content',
            animation: `rider-scroll ${duration}s linear infinite`,
          }}
          onMouseEnter={e => e.currentTarget.style.animationPlayState = 'paused'}
          onMouseLeave={e => e.currentTarget.style.animationPlayState = 'running'}
          onTouchStart={e => e.currentTarget.style.animationPlayState = 'paused'}
          onTouchEnd={e => e.currentTarget.style.animationPlayState = 'running'}
        >
          {cards.map((ev, i) => {
            const evDate = new Date(ev.event_date);
            const diff = Math.ceil((evDate - now) / (1000 * 60 * 60 * 24));
            const countdown = diff === 0 ? t('events.today') : diff === 1 ? t('events.tomorrow') : diff < 7 ? t('events.inDays').replace('{count}', diff) : evDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
            return (
              <button key={`${ev.id}-${i}`} onClick={() => navigateProtected(`/events/${ev.id}`)}
                className="flex-shrink-0 w-[270px] sm:w-[310px] rounded-2xl overflow-hidden text-left transition-all group hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 active:scale-[0.99]"
                style={{ background: 'rgba(12,22,22,0.90)', border: '1px solid rgba(6,182,212,0.18)' }}
                data-testid={i < sorted.length ? `rider-event-${ev.id}` : undefined}
              >
                <div className="flex" style={{ minHeight: 108 }}>
                  <div className="flex-1 p-3.5 flex items-start gap-2.5 min-w-0">
                    {/* Date badge */}
                    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-2 py-2 text-center min-w-[44px] flex-shrink-0">
                      <div className="text-cyan-300 font-black text-lg leading-none">{evDate.getDate()}</div>
                      <div className="text-cyan-400/70 text-[9px] font-bold uppercase mt-0.5">{evDate.toLocaleDateString(undefined, { month: 'short' })}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-bold text-sm leading-snug group-hover:text-cyan-300 transition-colors" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.title}</h4>
                      {/* Club logo + name (preferred) or creator name */}
                      {ev.club_logo ? (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <img
                            src={ev.club_logo.startsWith('http') ? ev.club_logo : `${process.env.REACT_APP_BACKEND_URL}${ev.club_logo}`}
                            alt={ev.club_name || ''}
                            className="w-5 h-5 rounded-full object-cover border border-white/15 flex-shrink-0"
                          />
                          <span className="text-zinc-300 text-[11px] font-semibold truncate">{ev.club_name}</span>
                        </div>
                      ) : ev.created_by_user_name ? (
                        <p className="text-zinc-400 text-[11px] mt-1 flex items-center gap-1 truncate">
                          <User className="w-2.5 h-2.5 flex-shrink-0" /> {ev.created_by_user_name}
                        </p>
                      ) : null}
                      {(ev.city || ev.location_name) && (
                        <p className="text-zinc-500 text-[11px] mt-0.5 flex items-center gap-1 truncate">
                          <MapPin className="w-2.5 h-2.5 flex-shrink-0" /> {ev.city || ev.location_name}
                        </p>
                      )}
                      <span className="inline-block mt-1.5 text-[9px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/12 text-cyan-400 border border-cyan-500/20">{countdown}</span>
                    </div>
                  </div>
                  <div className="w-[95px] flex-shrink-0">
                    <img loading="lazy" src={getEventImage(ev, i % sorted.length)} alt="" className="w-full h-full object-cover" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
    </ScrollReveal>
  );
}

function MapSection({ clubs, userLocation, locationLoading, requestLocation, radiusIndex, setRadiusIndex, currentRadius, navigate }) {
  const { t } = useLanguage();

  // Auto-request GPS when MapSection first mounts — non-blocking, user can deny silently
  useEffect(() => {
    if (!userLocation && !locationLoading) {
      requestLocation().catch(() => { /* GPS denied — map uses fallback center */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollReveal delay={100}>
    <section className="py-5 px-4" data-testid="map-section">
      <div className="max-w-4xl mx-auto">
        <div className="glass-card p-2 sm:p-3" style={{ background: 'rgba(15, 23, 22, 0.75)' }}>
          <div className="mb-2">
            <ErrorBoundary fallback={null}>
            <PermanentRadiusSlider
              radiusIndex={radiusIndex}
              onRadiusIndexChange={setRadiusIndex}
              currentRadius={currentRadius}
              userLocation={userLocation}
              locationLoading={locationLoading}
              onRequestLocation={requestLocation}
              compact={false}
              t={t}
            />
            </ErrorBoundary>
          </div>
          <ErrorBoundary fallback={
            <div className="w-full bg-zinc-900/50 rounded-2xl flex flex-col items-center justify-center gap-3" style={{height: 'max(56vh, 360px)'}}>
              <div className="text-zinc-400 text-sm">{t('gen.mapLoadError') || 'Kortet kunne ikke indlæses'}</div>
              <a href="/kort" className="text-emerald-400 text-sm underline">{t('gen.openMap') || 'Åbn fuldt kort'}</a>
            </div>
          }>
          <Suspense fallback={<div className="w-full bg-zinc-900/50 rounded-2xl flex items-center justify-center" style={{height: 'max(56vh, 360px)'}}><div className="text-zinc-500 text-sm">{t('gen.59f5d2a783')}</div></div>}>
          <ClubMap 
            clubs={userLocation ? (() => {
              const withDist = clubs.map(club => ({
                ...club,
                distance_km: calculateDistance(userLocation[0], userLocation[1], club.latitude, club.longitude)
              }));
              const inRange = withDist.filter(c => c.distance_km <= currentRadius);
              return inRange.length > 0 ? inRange : withDist;
            })() : clubs} 
            userLocation={userLocation}
            center={userLocation ? userLocation : [55.5, 10.3]}
            zoom={userLocation ? 10 : 7}
            height="max(56vh, 360px)"
            onClubClick={(club) => navigate(`/klubber/${club.id}`)}
          />
          </Suspense>
          </ErrorBoundary>
          {userLocation && (
            <div className="mt-2">
              <ErrorBoundary fallback={null}>
              <NearestClubCard
                clubs={clubs}
                userLocation={userLocation}
                radius={currentRadius}
                onSelectClub={(club) => navigate(`/klubber/${club.id}`)}
                t={t}
              />
              </ErrorBoundary>
            </div>
          )}
          {!userLocation && !locationLoading && (
            <button
              onClick={requestLocation}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-xs font-medium transition-colors"
              style={{ background: 'rgba(45,255,136,0.07)', color: 'rgba(45,255,136,0.7)', border: '1px solid rgba(45,255,136,0.15)' }}
              data-testid="enable-location-btn"
            >
              <MapPin className="w-3.5 h-3.5" />
              {t('landing.enableLocationPrompt')}
            </button>
          )}
        </div>
        <div className="text-center mt-4">
          <Link to="/kort">
            <button className="inline-flex items-center gap-2.5 bg-emerald-400 text-zinc-900 font-bold text-sm px-8 py-3.5 rounded-full hover:bg-emerald-300 transition-all shadow-lg shadow-emerald-400/20 hover:shadow-emerald-300/30 active:scale-[0.98]" data-testid="view-full-map-btn">
              <Map className="w-5 h-5" />
              {t('landing.viewFullMap')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </section>
    </ScrollReveal>
  );
}

function FarliSection({ language, showFarliCapabilities, setShowFarliCapabilities }) {
  const { t } = useLanguage();
  return (
    <ScrollReveal>
    <section className="py-8 px-4" data-testid="farli-section">
      <div className="max-w-4xl mx-auto">
        <div className="glass-card p-4 sm:p-5">
          <div className="flex items-center gap-4 sm:gap-5">
            {/* Compact portrait */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden flex-shrink-0 shadow-lg border border-emerald-400/20">
              <img 
                src="https://customer-assets.emergentagent.com/job_1cbcbc5e-78bb-434a-b13e-22e5d4e75fba/artifacts/m06phxku_8854F95E-FBD9-4755-BFCF-9D397BC7C8A0.jpeg" 
                alt="Farli" 
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">{t('landing.farliTitle')}</h3>
              <p className="text-white/65 text-xs sm:text-sm mb-3 leading-relaxed line-clamp-2 sm:line-clamp-3">
                <EditableBlock pageId="landing" sectionId="farliDesc" defaultText={t('landing.farliDesc')} />
              </p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300">{t('landing.farliTag1')}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300">{t('landing.farliTag2')}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300">{t('landing.farliTag3')}</span>
              </div>
              <button
                onClick={() => setShowFarliCapabilities(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 hover:border-emerald-500/50 transition-all text-xs font-semibold"
                data-testid="landing-farli-capabilities-btn"
              >
                {t('landing.seeWhatFarliCan')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    </ScrollReveal>
  );
}

function OpenNowSection({ openClubs }) {
  const { t } = useLanguage();
  return (
    <ScrollReveal>
    <section className="py-8 px-4" data-testid="open-now-section">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 pulse-open flex-shrink-0" />
            <h2 className="text-lg font-bold text-white">{t('landing.openRightNow')}</h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">{openClubs.length}</span>
          </div>
          <Link to="/klubber?open=true">
            <button className="text-xs font-medium text-white/60 hover:text-white flex items-center gap-1 transition-colors" data-testid="see-all-open-clubs">
              {t('common.seeAll')} <ChevronRight className="w-3 h-3" />
            </button>
          </Link>
        </div>
        <div className="space-y-2">
          {openClubs.slice(0, 5).map(club => (
            <Link to={`/klubber/${club.id}`} key={club.id} data-testid={`open-now-club-${club.id}`}>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/60 border border-white/[0.08] hover:border-emerald-500/25 hover:bg-zinc-900/80 transition-all group">
                {club.logo_url || club.image_url ? (
                  <img loading="lazy" src={getImageUrl(club.logo_url || club.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-emerald-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate group-hover:text-emerald-300 transition-colors">{club.name}</div>
                  {(club.city || club.address) && (
                    <div className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-0.5">
                      <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{club.city || club.address}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <span className="text-[11px] font-semibold text-green-400">{t('status.open')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
    </ScrollReveal>
  );
}

function NewsletterShareSection() {
  const { t } = useLanguage();
  return (
    <div>
      <ScrollReveal>
      <section className="py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card-light p-6 border border-white/10">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-lg font-bold text-cream mb-2">{t('landing.newsletter')}</h3>
                <p className="text-cream-muted text-sm mb-3">
                  <EditableBlock pageId="landing" sectionId="newsletterDesc" defaultText={t('landing.newsletterDesc2')} />
                </p>
                <div className="flex flex-col gap-1.5 text-xs text-cream-muted/70">
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t('landing.newsletterRegions')}</span>
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t('landing.newsletterEvents')}</span>
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t('landing.newsletterUnsubscribe')}</span>
                </div>
              </div>
              <NewsletterSignup />
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>
      <ScrollReveal delay={100}>
      <section className="py-6 px-4">
        <div className="max-w-md mx-auto">
          <ShareButton />
        </div>
      </section>
      </ScrollReveal>
    </div>
  );
}

function PitstopCTASection({ navigate, onOpenWizard }) {
  const { t } = useLanguage();
  const pillCategories = [
    { emoji: '☕', key: 'subCafe' },
    { emoji: '🍽️', key: 'subRestaurant' },
    { emoji: '🍔', key: 'subGrill' },
    { emoji: '🍦', key: 'subIsbar' },
    { emoji: '🍕', key: 'subPizzeria' },
    { emoji: '🍺', key: 'subBar' },
    { emoji: '🛏️', key: 'catOvernatning' },
    { emoji: '⛽', key: 'subTankstation' },
    { emoji: '🔧', key: 'subVaerksted' },
  ];
  return (
    <section className="py-14 border-t border-white/5" data-testid="pitstop-section">
      <div className="max-w-4xl mx-auto px-4">
        <div className="relative rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(6,78,59,0.9) 0%, rgba(4,47,36,0.95) 100%)' }}>
          <div className="absolute inset-0 rounded-3xl opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%2310b981\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="relative p-8 sm:p-12 pb-10 sm:pb-14 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-xs font-bold mb-6 tracking-wider uppercase">
              <MapPin className="w-3.5 h-3.5" />
              {t('pitstop.ctaRegisterLabel')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3 leading-tight">
              {t('pitstop.ctaTitle')}
            </h2>
            <p className="text-white/70 text-sm sm:text-base max-w-xl mx-auto mb-8 leading-relaxed">
              {t('pitstop.ctaDescription')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={onOpenWizard || (() => navigate('/opret-profil'))}
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-zinc-900 font-bold text-sm rounded-2xl hover:bg-emerald-400 transition-all active:scale-[0.97] shadow-lg shadow-emerald-500/25"
                data-testid="pitstop-cta-btn"
              >
                <MapPin className="w-4 h-4" />
                {t('pitstop.ctaCreateProfile')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-8">
              {pillCategories.map(item => (
                <div
                  key={item.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 border border-white/12 text-sm text-white/75"
                >
                  <span>{item.emoji}</span>
                  <span className="font-medium">{t(`wizard.${item.key}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OvernightFreeSection({ overnightClubs, navigate }) {
  const { t } = useLanguage();
  const freeClubs = overnightClubs.filter(c => c.overnight_free);
  if (freeClubs.length === 0) return null;
  return (
    <section className="relative z-10 max-w-5xl mx-auto px-4 py-12 mt-4" data-testid="free-overnight-section">
      <div className="mb-6">
        <h2 className="text-white font-black text-xl sm:text-2xl flex items-center gap-2.5">
          <Moon className="w-6 h-6 text-emerald-400" />
          {t('overnight.freeOvernight')}
        </h2>
        <p className="text-white/60 text-sm mt-2 max-w-lg leading-relaxed">
          {t('overnight.freeDesc')}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {freeClubs.slice(0, 3).map(club => (
          <button
            key={`free-${club.id}`}
            onClick={() => navigate(club.is_pitstop ? `/pitstop/${club.id}` : `/klubber/${club.id}`)}
            className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-900/20 border border-emerald-500/15 hover:border-emerald-500/40 hover:bg-emerald-900/30 transition-all text-left group"
            data-testid={`free-overnight-${club.id}`}
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              {club.logo_url || club.image_url ? (
                <img loading="lazy" src={getImageUrl(club.logo_url || club.image_url)} alt=""  className="w-full h-full rounded-xl object-cover" />
              ) : (
                <Moon className="w-5 h-5 text-emerald-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold truncate group-hover:text-emerald-300 transition-colors">{club.name}</p>
              <p className="text-white/50 text-xs mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{club.city || club.address} {club.country && countryToFlag(club.country)}</span>
              </p>
            </div>
            <span className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-bold flex-shrink-0">{t('overnight.free')}</span>
          </button>
        ))}
      </div>
      <button 
        onClick={() => navigate('/kort')}
        className="mt-4 w-full py-3 rounded-xl border border-emerald-500/20 text-emerald-400 text-sm font-semibold hover:bg-emerald-500/10 transition-all flex items-center justify-center gap-2"
      >
        {t('overnight.seeAllFree').replace('{count}', freeClubs.length)} <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function OvernightPaidSection({ overnightClubs, navigate }) {
  const { t } = useLanguage();
  const paidClubs = overnightClubs.filter(c => !c.overnight_free && c.overnight_price > 0);
  if (paidClubs.length === 0) return null;
  return (
    <section className="relative z-10 max-w-5xl mx-auto px-4 py-12 mt-2" data-testid="paid-overnight-section">
      <div className="mb-6">
        <h2 className="text-white font-black text-xl sm:text-2xl flex items-center gap-2.5">
          <Moon className="w-6 h-6 text-amber-400" />
          {t('overnight.paidOvernight')}
        </h2>
        <p className="text-white/60 text-sm mt-2 max-w-lg leading-relaxed">
          {t('overnight.paidDesc')}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {paidClubs.slice(0, 3).map(club => (
          <button
            key={`paid-${club.id}`}
            onClick={() => navigate(club.is_pitstop ? `/pitstop/${club.id}` : `/klubber/${club.id}`)}
            className="flex items-center gap-4 p-4 rounded-2xl bg-amber-900/15 border border-amber-500/15 hover:border-amber-500/40 hover:bg-amber-900/25 transition-all text-left group"
            data-testid={`paid-overnight-${club.id}`}
          >
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
              {club.logo_url || club.image_url ? (
                <img loading="lazy" src={getImageUrl(club.logo_url || club.image_url)} alt=""  className="w-full h-full rounded-xl object-cover" />
              ) : (
                <Moon className="w-5 h-5 text-amber-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold truncate group-hover:text-amber-300 transition-colors">{club.name}</p>
              <p className="text-white/50 text-xs mt-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{club.city || club.address} {club.country && countryToFlag(club.country)}</span>
              </p>
            </div>
            <span className="px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-400 text-xs font-bold flex-shrink-0">{t('overnight.priceKr').replace('{price}', club.overnight_price)}</span>
          </button>
        ))}
      </div>
      <button 
        onClick={() => navigate('/kort')}
        className="mt-4 w-full py-3 rounded-xl border border-amber-500/20 text-amber-400 text-sm font-semibold hover:bg-amber-500/10 transition-all flex items-center justify-center gap-2"
      >
        {t('overnight.seeAllOvernight').replace('{count}', overnightClubs.length)} <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function DownloadAppSection({ navigate }) {
  const { t } = useLanguage();
  return (
    <section className="relative z-10 max-w-5xl mx-auto px-4 py-16 mt-4" data-testid="download-app-section">
      <div 
        onClick={() => navigate('/hjaelp')}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-900/40 to-emerald-800/20 border border-emerald-500/20 p-6 sm:p-8 cursor-pointer hover:border-emerald-500/40 transition-all group"
        data-testid="download-app-cta"
      >
        <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative flex flex-col sm:flex-row items-center gap-6">
          <div className="w-32 sm:w-40 flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/867a33cd-73ed-4e5a-9d8e-272eab489b4f/images/86fca9ff381c1244a9fa2d2b5c8e587a52c4235f6c84d9d1584f03924dd03dc0.png" 
              alt="BareMC app" 
              className="w-full h-auto drop-shadow-2xl"
            />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-white font-black text-lg sm:text-xl">
              {t('download.takeWithYou')}
            </h3>
            <p className="text-white/60 text-sm mt-1.5 max-w-md leading-relaxed">
              {t('download.desc')}
            </p>
            <div className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-xl bg-emerald-500 text-black font-bold text-sm group-hover:bg-emerald-400 transition-all">
              <Download className="w-4 h-4" />
              {t('download.seeHow')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingPage;
